package com.partejar.app.feature.convite.presentation

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.PasswordVisualTransformation
import com.partejar.app.core.ui.components.passwordKeyboardOptions
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.core.ui.theme.ThemeMode
import com.partejar.app.feature.convite.data.ConviteAceiteDetails
import org.koin.compose.koinInject

@Composable
fun ConviteAceiteScreen(
    token: String,
    onBack: () -> Unit,
    onSignIn: () -> Unit,
    onDestinationResolved: (SessionDestination) -> Unit,
    viewModel: ConviteAceiteViewModel = koinInject()
) {
    val state by viewModel.uiState.collectAsState()
    val action by viewModel.actionState.collectAsState()
    LaunchedEffect(token) { viewModel.load(token) }
    LaunchedEffect(action) {
        val success = action as? ConviteAceiteActionState.Success
        if (success != null) {
            onDestinationResolved(success.destination)
            viewModel.clearAction()
        }
    }
    ConviteAceiteLayout(
        state = state,
        action = action,
        hasSession = viewModel.hasSession(),
        onBack = onBack,
        onRetry = { viewModel.load(token) },
        onSignIn = onSignIn,
        onAcceptNew = { name, password, confirmation -> viewModel.acceptNewUser(token, name, password, confirmation) },
        onAcceptExisting = { viewModel.acceptExistingUser(token) },
        onClearAction = viewModel::clearAction
    )
}

@Composable
private fun ConviteAceiteLayout(
    state: ConviteAceiteUiState,
    action: ConviteAceiteActionState,
    hasSession: Boolean,
    onBack: () -> Unit,
    onRetry: () -> Unit,
    onSignIn: () -> Unit,
    onAcceptNew: (String, String, String) -> Unit,
    onAcceptExisting: () -> Unit,
    onClearAction: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = "Convite para a equipe",
                leadingContent = {
                    IconButton(onClick = onBack) {
                        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
                    }
                }
            )
        }
    ) { padding ->
        when (state) {
            ConviteAceiteUiState.Loading -> androidx.compose.foundation.layout.Box(
                Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center
            ) { CircularProgressIndicator() }
            is ConviteAceiteUiState.Error -> Column(
                Modifier.fillMaxSize().padding(padding).padding(spacing.space24),
                verticalArrangement = Arrangement.spacedBy(spacing.space16)
            ) {
                PartejarAlert(title = "Convite indisponível", tone = PartejarAlertTone.ERROR) { Text(state.message) }
                PartejarButton("Tentar novamente", onRetry, variant = PartejarButtonVariant.SECONDARY)
            }
            is ConviteAceiteUiState.Content -> InviteContent(
                details = state.details,
                action = action,
                hasSession = hasSession,
                padding = padding,
                onSignIn = onSignIn,
                onAcceptNew = onAcceptNew,
                onAcceptExisting = onAcceptExisting,
                onClearAction = onClearAction
            )
        }
    }
}

@Composable
private fun InviteContent(
    details: ConviteAceiteDetails,
    action: ConviteAceiteActionState,
    hasSession: Boolean,
    padding: PaddingValues,
    onSignIn: () -> Unit,
    onAcceptNew: (String, String, String) -> Unit,
    onAcceptExisting: () -> Unit,
    onClearAction: () -> Unit
) {
    val spacing = PartejarTheme.spacing
    var name by remember(details.email) { mutableStateOf(details.invitedName) }
    var password by remember { mutableStateOf("") }
    var confirmation by remember { mutableStateOf("") }
    Column(
        Modifier.fillMaxSize().padding(padding).verticalScroll(rememberScrollState()).padding(spacing.space16),
        verticalArrangement = Arrangement.spacedBy(spacing.space16)
    ) {
        Text("Você foi convidada", style = MaterialTheme.typography.headlineSmall, modifier = Modifier.semantics { heading() })
        Text("Revise os dados antes de entrar neste espaço.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
        PartejarCard(modifier = Modifier.fillMaxWidth()) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                Text(details.workspaceName, style = MaterialTheme.typography.titleMedium)
                DetailLine("Papel", details.profileName)
                DetailLine("Categoria", details.professionalCategory.replace('_', ' '))
                DetailLine("E-mail", details.email)
            }
        }
        if (action is ConviteAceiteActionState.Error) {
            PartejarAlert(title = "Não foi possível concluir", tone = PartejarAlertTone.ERROR) { Text(action.message) }
            PartejarButton("Dispensar", onClearAction, variant = PartejarButtonVariant.TERTIARY)
        }
        when {
            details.existingUser && !hasSession -> {
                PartejarAlert(title = "Sua conta já existe", tone = PartejarAlertTone.INFO) {
                    Text("Entre com o e-mail convidado para confirmar sua identidade e aceitar o convite.")
                }
                PartejarButton("Entrar com minha conta", onSignIn)
            }
            details.existingUser -> {
                PartejarButton("Aceitar convite", onAcceptExisting, isLoading = action is ConviteAceiteActionState.Submitting)
            }
            else -> {
                PartejarTextField(name, { name = it }, "Seu nome", isError = action is ConviteAceiteActionState.Error)
                PartejarTextField(details.email, {}, "E-mail convidado", enabled = false)
                PartejarTextField(password, { password = it }, "Crie uma senha", keyboardOptions = passwordKeyboardOptions, visualTransformation = PasswordVisualTransformation(), isError = action is ConviteAceiteActionState.Error)
                PartejarTextField(confirmation, { confirmation = it }, "Confirme sua senha", keyboardOptions = passwordKeyboardOptions, visualTransformation = PasswordVisualTransformation(), isError = action is ConviteAceiteActionState.Error)
                PartejarButton("Criar acesso e aceitar", { onAcceptNew(name, password, confirmation) }, isLoading = action is ConviteAceiteActionState.Submitting)
            }
        }
        Spacer(Modifier.height(spacing.space24))
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    Text("$label: $value", style = MaterialTheme.typography.bodyMedium)
}

@Preview
@Composable
private fun ConviteAceiteLightPreview() = PartejarTheme(ThemeMode.LIGHT) {
    ConviteAceiteLayout(
        ConviteAceiteUiState.Content(sampleInvite()), ConviteAceiteActionState.Idle, false,
        {}, {}, {}, { _, _, _ -> }, {}, {}
    )
}

private fun sampleInvite() = ConviteAceiteDetails("Ana", "ana@partejar.com", "Casa Partejar", "Equipe clínica", "DOULA", "2026-08-20", false)
